import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

// ─── Constants ────────────────────────────────────────────────────────────────
const WORKFLOW_STEPS = [
  { id: 1, label: 'Type de profil', icon: 'UserCheck', description: 'Choisissez votre rôle professionnel' },
  { id: 2, label: 'Documents identité', icon: 'FileText', description: 'Pièce d\'identité et justificatifs' },
  { id: 3, label: 'Certifications', icon: 'Award', description: 'Licences et accréditations professionnelles' },
  { id: 4, label: 'Vérification', icon: 'ShieldCheck', description: 'Checklist de conformité' },
  { id: 5, label: 'Soumission', icon: 'Send', description: 'Envoi pour approbation' },
];

const AGENT_CHECKLIST = [
  { id: 'id_doc', label: 'Pièce d\'identité valide (CNI ou Passeport)', required: true },
  { id: 'license', label: 'Carte professionnelle d\'agent immobilier', required: true },
  { id: 'tax_cert', label: 'Attestation fiscale en cours de validité', required: true },
  { id: 'insurance', label: 'Attestation d\'assurance responsabilité civile', required: false },
  { id: 'photo', label: 'Photo professionnelle récente', required: true },
  { id: 'address_proof', label: 'Justificatif de domicile professionnel', required: false },
];

const NOTARY_CHECKLIST = [
  { id: 'id_doc', label: 'Pièce d\'identité valide (CNI ou Passeport)', required: true },
  { id: 'notary_license', label: 'Diplôme de notaire / Acte de nomination', required: true },
  { id: 'bar_cert', label: 'Certificat d\'inscription à la Chambre des Notaires', required: true },
  { id: 'insurance', label: 'Assurance responsabilité professionnelle', required: true },
  { id: 'tax_cert', label: 'Attestation fiscale en cours de validité', required: true },
  { id: 'office_proof', label: 'Justificatif d\'adresse de l\'étude notariale', required: false },
];

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: 'Edit3' },
  submitted: { label: 'Soumis', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'Clock' },
  under_review: { label: 'En cours d\'examen', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'Search' },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-700 border-green-200', icon: 'CheckCircle' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700 border-red-200', icon: 'XCircle' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center mb-8 overflow-x-auto pb-2">
    {WORKFLOW_STEPS?.map((step, idx) => (
      <React.Fragment key={step?.id}>
        <div className="flex flex-col items-center min-w-[60px]">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
            currentStep > step?.id ? 'bg-green-500 text-white shadow-md' :
            currentStep === step?.id ? 'bg-amber-500 text-white shadow-md ring-4 ring-amber-500/20': 'bg-gray-100 border-2 border-gray-200 text-gray-400'
          }`}>
            {currentStep > step?.id ? <Icon name="Check" size={16} strokeWidth={2.5} /> : <Icon name={step?.icon} size={16} />}
          </div>
          <span className={`mt-1.5 text-[10px] font-medium text-center leading-tight max-w-[60px] ${
            currentStep === step?.id ? 'text-amber-600' : currentStep > step?.id ? 'text-green-600' : 'text-gray-400'
          }`}>{step?.label}</span>
        </div>
        {idx < WORKFLOW_STEPS?.length - 1 && (
          <div className={`flex-1 h-0.5 mx-1 mb-5 rounded-full min-w-[20px] transition-all duration-300 ${
            currentStep > step?.id ? 'bg-green-400' : 'bg-gray-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const FileUploadZone = ({ label, hint, accept, onFile, file, uploading, uploaded }) => {
  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    const f = e?.dataTransfer?.files?.[0];
    if (f) onFile(f);
  }, [onFile]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e?.preventDefault()}
      onClick={() => document.getElementById(`cred-file-${label}`)?.click()}
      className={`relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-200 hover:border-amber-400 hover:bg-amber-50/30 ${
        uploaded ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50/50'
      }`}
    >
      <input id={`cred-file-${label}`} type="file" accept={accept} className="hidden" onChange={(e) => onFile(e?.target?.files?.[0])} />
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs text-gray-500">Téléchargement...</span>
        </div>
      ) : uploaded ? (
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Icon name="CheckCircle" size={22} className="text-green-600" />
          </div>
          <span className="text-xs text-green-700 font-medium">{file?.name || 'Fichier téléchargé'}</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200 shadow-sm">
            <Icon name="Upload" size={18} className="text-gray-400" />
          </div>
          <span className="text-sm font-medium text-gray-700">{label}</span>
          {hint && <span className="text-xs text-gray-400">{hint}</span>}
        </div>
      )}
    </div>
  );
};

const ChecklistItem = ({ item, checked, onChange }) => (
  <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
    checked ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/30'
  }`}>
    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
      checked ? 'bg-green-500 border-green-500' : 'border-gray-300'
    }`}>
      {checked && <Icon name="Check" size={12} className="text-white" strokeWidth={3} />}
    </div>
    <input type="checkbox" className="hidden" checked={checked} onChange={onChange} />
    <div className="flex-1">
      <span className={`text-sm font-medium ${checked ? 'text-green-800' : 'text-gray-700'}`}>{item?.label}</span>
      {item?.required && <span className="ml-2 text-[10px] text-red-500 font-semibold">REQUIS</span>}
    </div>
  </label>
);

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={12} />
      {cfg?.label}
    </span>
  );
};

// ─── Approval Status Tracker ──────────────────────────────────────────────────
const ApprovalStatusTracker = ({ application }) => {
  const steps = [
    { key: 'submitted', label: 'Soumis', icon: 'Send', desc: 'Dossier reçu par l\'équipe' },
    { key: 'under_review', label: 'En examen', icon: 'Search', desc: 'Vérification des documents' },
    { key: 'approved', label: 'Approuvé', icon: 'CheckCircle', desc: 'Accès à la plateforme accordé' },
  ];
  const currentIdx = application?.status === 'rejected' ? -1 :
    steps?.findIndex(s => s?.key === application?.status);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base font-semibold text-gray-900">Suivi de votre dossier</h3>
        <StatusBadge status={application?.status} />
      </div>

      {application?.status === 'rejected' ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Icon name="XCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">Dossier refusé</p>
              <p className="text-sm text-red-600 mt-1">{application?.rejection_reason || 'Veuillez contacter l\'équipe pour plus d\'informations.'}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-gray-100" />
          <div className="space-y-4">
            {steps?.map((step, idx) => {
              const done = idx <= currentIdx;
              const active = idx === currentIdx;
              return (
                <div key={step?.key} className="flex items-start gap-4 relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-all ${
                    done ? 'bg-green-500 text-white shadow-md' :
                    active ? 'bg-amber-500 text-white shadow-md ring-4 ring-amber-500/20': 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                  }`}>
                    <Icon name={step?.icon} size={16} />
                  </div>
                  <div className="flex-1 pt-1.5">
                    <p className={`text-sm font-semibold ${done ? 'text-green-700' : active ? 'text-amber-700' : 'text-gray-400'}`}>{step?.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{step?.desc}</p>
                    {active && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse" />
                        <span className="text-xs text-amber-600 font-medium">En cours...</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {application?.status === 'approved' && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-3">
            <Icon name="PartyPopper" size={20} className="text-green-600" />
            <div>
              <p className="text-sm font-semibold text-green-800">Félicitations ! Votre compte est activé.</p>
              <p className="text-xs text-green-600 mt-0.5">Vous pouvez maintenant accéder à toutes les fonctionnalités de la plateforme.</p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3 text-xs text-gray-500">
        <div>
          <span className="font-medium text-gray-600">Référence :</span>
          <span className="ml-1 font-mono">{application?.id?.slice(0, 8)?.toUpperCase()}</span>
        </div>
        <div>
          <span className="font-medium text-gray-600">Soumis le :</span>
          <span className="ml-1">{application?.submitted_at ? new Date(application.submitted_at)?.toLocaleDateString('fr-FR') : '—'}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const CredentialSubmissionWorkflow = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [toast, setToast] = useState(null);

  // Step 1
  const [profileType, setProfileType] = useState('');

  // Step 2 — Identity docs
  const [idFile, setIdFile] = useState(null);
  const [idUploading, setIdUploading] = useState(false);
  const [idUploaded, setIdUploaded] = useState(false);
  const [idUrl, setIdUrl] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');

  // Step 3 — Certifications
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const [licenseUploaded, setLicenseUploaded] = useState(false);
  const [licenseUrl, setLicenseUrl] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [additionalCertFile, setAdditionalCertFile] = useState(null);
  const [additionalCertUploading, setAdditionalCertUploading] = useState(false);
  const [additionalCertUploaded, setAdditionalCertUploaded] = useState(false);
  const [additionalCertUrl, setAdditionalCertUrl] = useState('');

  // Step 4 — Checklist
  const [checkedItems, setCheckedItems] = useState({});

  const checklist = profileType === 'notary' ? NOTARY_CHECKLIST : AGENT_CHECKLIST;

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Check for existing application
  useEffect(() => {
    const checkExisting = async () => {
      if (!user) { setCheckingExisting(false); return; }
      try {
        const { data } = await supabase?.from('professional_applications')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle();
        if (data) setExistingApplication(data);
      } catch (err) {
        console.error('Error checking existing application:', err);
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExisting();
  }, [user]);

  // Realtime subscription for status updates
  useEffect(() => {
    if (!existingApplication?.id) return;
    const channel = supabase?.channel(`credential-app-${existingApplication?.id}`)?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'professional_applications',
        filter: `id=eq.${existingApplication?.id}`,
      }, (payload) => {
        setExistingApplication(prev => ({ ...prev, ...payload?.new }));
        if (payload?.new?.status === 'approved') showToast('🎉 Votre dossier a été approuvé !', 'success');
        if (payload?.new?.status === 'under_review') showToast('Votre dossier est en cours d\'examen.', 'info');
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [existingApplication?.id]);

  const uploadFile = async (file, bucket, folder, setUploading, setUploaded, setUrl) => {
    if (!file || !user) return null;
    setUploading(true);
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `${folder}/${user?.id}/${Date.now()}.${ext}`;
      const { error } = await supabase?.storage?.from(bucket)?.upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: urlData } = supabase?.storage?.from(bucket)?.getPublicUrl(path);
      setUploaded(true);
      setUrl(urlData?.publicUrl || '');
      return urlData?.publicUrl || '';
    } catch (err) {
      console.error('Upload error:', err);
      showToast('Erreur lors du téléchargement. Réessayez.', 'error');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleIdUpload = (file) => {
    if (file) uploadFile(file, 'professional-licenses', 'id-docs', setIdUploading, setIdUploaded, setIdUrl);
    setIdFile(file);
  };

  const handlePhotoUpload = (file) => {
    if (file) uploadFile(file, 'professional-avatars', 'photos', setPhotoUploading, setPhotoUploaded, setPhotoUrl);
    setPhotoFile(file);
  };

  const handleLicenseUpload = (file) => {
    if (file) uploadFile(file, 'professional-certifications', 'licenses', setLicenseUploading, setLicenseUploaded, setLicenseUrl);
    setLicenseFile(file);
  };

  const handleAdditionalCertUpload = (file) => {
    if (file) uploadFile(file, 'professional-certifications', 'additional', setAdditionalCertUploading, setAdditionalCertUploaded, setAdditionalCertUrl);
    setAdditionalCertFile(file);
  };

  const canProceedStep2 = idUploaded && photoUploaded;
  const canProceedStep3 = licenseUploaded;
  const requiredChecked = checklist?.filter(i => i?.required)?.every(i => checkedItems?.[i?.id]);
  const totalChecked = Object.values(checkedItems)?.filter(Boolean)?.length;

  const handleSubmit = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const payload = {
        user_id: user?.id,
        full_name: userProfile?.full_name || '',
        email: userProfile?.email || user?.email,
        phone: userProfile?.phone || '',
        professional_type: profileType,
        license_number: licenseNumber,
        id_document_url: idUrl,
        license_url: licenseUrl,
        additional_cert_url: additionalCertUrl,
        photo_url: photoUrl,
        checklist_completed: checkedItems,
        status: 'submitted',
        submitted_at: new Date()?.toISOString(),
        application_status: 'submitted',
      };

      let result;
      if (existingApplication?.id) {
        const { data, error } = await supabase?.from('professional_applications')?.update(payload)?.eq('id', existingApplication?.id)?.select()?.single();
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase?.from('professional_applications')?.insert(payload)?.select()?.single();
        if (error) throw error;
        result = data;
      }

      setExistingApplication(result);
      setStep(5);
      showToast('Dossier soumis avec succès ! Vous recevrez une notification lors de l\'examen.', 'success');
    } catch (err) {
      console.error('Submit error:', err);
      showToast('Erreur lors de la soumission. Réessayez.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If already has a submitted/reviewed/approved application, show status tracker
  if (existingApplication && ['submitted', 'under_review', 'approved', 'rejected']?.includes(existingApplication?.status)) {
    return (
      <>
        <Helmet><title>Statut de votre dossier - KapitalEstates</title></Helmet>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-20 pb-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name="FileSearch" size={28} className="text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Suivi de votre dossier</h1>
              <p className="text-gray-500 mt-2">Suivez l'avancement de votre demande d'accréditation professionnelle</p>
            </div>

            <ApprovalStatusTracker application={existingApplication} />

            {existingApplication?.status === 'rejected' && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => { setExistingApplication(null); setStep(1); }}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-md"
                >
                  Soumettre un nouveau dossier
                </button>
              </div>
            )}

            {existingApplication?.status === 'approved' && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/professional-dashboard')}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-md"
                >
                  Accéder à mon espace professionnel
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Soumettre mes credentials - KapitalEstates</title></Helmet>
      <Header />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${
          toast?.type === 'error' ? 'bg-red-500 text-white' :
          toast?.type === 'info'? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
        }`}>
          <Icon name={toast?.type === 'error' ? 'XCircle' : toast?.type === 'info' ? 'Info' : 'CheckCircle'} size={16} />
          {toast?.msg}
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-20 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Icon name="ShieldCheck" size={28} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Accréditation Professionnelle</h1>
            <p className="text-gray-500 mt-2 text-sm">Soumettez vos credentials pour accéder à la plateforme KapitalEstates</p>
          </div>

          <StepIndicator currentStep={step} />

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Step header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Icon name={WORKFLOW_STEPS?.[step - 1]?.icon} size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Étape {step} / {WORKFLOW_STEPS?.length}</p>
                  <p className="text-white/80 text-xs">{WORKFLOW_STEPS?.[step - 1]?.description}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* ── Step 1: Profile Type ── */}
              {step === 1 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Quel est votre rôle professionnel ?</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { value: 'agent', label: 'Agent Immobilier', icon: 'Briefcase', desc: 'Vente, location, gestion de biens immobiliers', color: 'from-amber-400 to-orange-400' },
                      { value: 'notary', label: 'Notaire', icon: 'Scale', desc: 'Actes notariés, séquestre, transactions légales', color: 'from-purple-400 to-indigo-400' },
                    ]?.map(opt => (
                      <button
                        key={opt?.value}
                        onClick={() => setProfileType(opt?.value)}
                        className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                          profileType === opt?.value
                            ? 'border-amber-400 bg-amber-50 shadow-md'
                            : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/30'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${opt?.color} flex items-center justify-center mb-3 shadow-sm`}>
                          <Icon name={opt?.icon} size={22} className="text-white" />
                        </div>
                        <p className="font-semibold text-gray-900">{opt?.label}</p>
                        <p className="text-xs text-gray-500 mt-1">{opt?.desc}</p>
                        {profileType === opt?.value && (
                          <div className="mt-2 flex items-center gap-1 text-amber-600">
                            <Icon name="CheckCircle" size={14} />
                            <span className="text-xs font-medium">Sélectionné</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Step 2: Identity Documents ── */}
              {step === 2 && (
                <div className="space-y-5">
                  <h2 className="text-lg font-semibold text-gray-900">Documents d'identité</h2>
                  <p className="text-sm text-gray-500">Téléchargez vos documents d'identité officiels. Formats acceptés : PDF, JPG, PNG.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FileUploadZone
                      label="Pièce d'identité *"
                      hint="CNI, Passeport ou Titre de séjour"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onFile={handleIdUpload}
                      file={idFile}
                      uploading={idUploading}
                      uploaded={idUploaded}
                    />
                    <FileUploadZone
                      label="Photo professionnelle *"
                      hint="Photo récente, fond neutre"
                      accept=".jpg,.jpeg,.png"
                      onFile={handlePhotoUpload}
                      file={photoFile}
                      uploading={photoUploading}
                      uploaded={photoUploaded}
                    />
                  </div>
                  {!canProceedStep2 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1.5">
                      <Icon name="AlertCircle" size={13} />
                      Les deux documents sont requis pour continuer.
                    </p>
                  )}
                </div>
              )}

              {/* ── Step 3: Certifications ── */}
              {step === 3 && (
                <div className="space-y-5">
                  <h2 className="text-lg font-semibold text-gray-900">Certifications professionnelles</h2>
                  <p className="text-sm text-gray-500">Téléchargez votre licence professionnelle et tout document de certification supplémentaire.</p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro de licence / matricule professionnel
                    </label>
                    <input
                      type="text"
                      value={licenseNumber}
                      onChange={(e) => setLicenseNumber(e?.target?.value)}
                      placeholder="Ex: CI-AGT-2024-00123"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FileUploadZone
                      label={profileType === 'notary' ? 'Diplôme / Acte de nomination *' : 'Carte professionnelle *'}
                      hint="Document officiel d'accréditation"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onFile={handleLicenseUpload}
                      file={licenseFile}
                      uploading={licenseUploading}
                      uploaded={licenseUploaded}
                    />
                    <FileUploadZone
                      label="Certification supplémentaire"
                      hint="Assurance, attestation fiscale, etc."
                      accept=".pdf,.jpg,.jpeg,.png"
                      onFile={handleAdditionalCertUpload}
                      file={additionalCertFile}
                      uploading={additionalCertUploading}
                      uploaded={additionalCertUploaded}
                    />
                  </div>
                  {!canProceedStep3 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1.5">
                      <Icon name="AlertCircle" size={13} />
                      La licence professionnelle est requise pour continuer.
                    </p>
                  )}
                </div>
              )}

              {/* ── Step 4: Verification Checklist ── */}
              {step === 4 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Checklist de vérification</h2>
                    <span className="text-sm text-gray-500 font-medium">{totalChecked} / {checklist?.length}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
                    <div
                      className="bg-gradient-to-r from-amber-400 to-orange-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(totalChecked / checklist?.length) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500">Confirmez que vous avez bien préparé tous les documents requis.</p>
                  <div className="space-y-2">
                    {checklist?.map(item => (
                      <ChecklistItem
                        key={item?.id}
                        item={item}
                        checked={!!checkedItems?.[item?.id]}
                        onChange={(e) => setCheckedItems(prev => ({ ...prev, [item?.id]: e?.target?.checked }))}
                      />
                    ))}
                  </div>
                  {!requiredChecked && (
                    <p className="text-xs text-red-500 flex items-center gap-1.5 mt-2">
                      <Icon name="AlertCircle" size={13} />
                      Veuillez cocher tous les éléments requis avant de continuer.
                    </p>
                  )}
                </div>
              )}

              {/* ── Step 5: Confirmation ── */}
              {step === 5 && (
                <div className="text-center py-4 space-y-5">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Icon name="CheckCircle" size={40} className="text-green-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Dossier soumis avec succès !</h2>
                    <p className="text-gray-500 mt-2 text-sm">Notre équipe va examiner votre dossier dans les 24-48h ouvrées. Vous serez notifié par email et dans l'application.</p>
                  </div>
                  {existingApplication && <ApprovalStatusTracker application={existingApplication} />}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => navigate('/')}
                      className="px-6 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Retour à l'accueil
                    </button>
                    <button
                      onClick={() => navigate('/professional-dashboard')}
                      className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-md"
                    >
                      Mon espace professionnel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation buttons */}
            {step < 5 && (
              <div className="flex items-center justify-between px-6 pb-6 pt-2 border-t border-gray-100">
                <button
                  onClick={() => setStep(s => Math.max(1, s - 1))}
                  disabled={step === 1}
                  className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" size={16} />
                  Précédent
                </button>

                {step < 4 ? (
                  <button
                    onClick={() => setStep(s => s + 1)}
                    disabled={
                      (step === 1 && !profileType) ||
                      (step === 2 && !canProceedStep2) ||
                      (step === 3 && !canProceedStep3)
                    }
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Suivant
                    <Icon name="ChevronRight" size={16} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={!requiredChecked || loading}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Send" size={16} />}
                    Soumettre le dossier
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CredentialSubmissionWorkflow;
